import {
  elementScroll,
  useVirtualizer,
  type VirtualizerOptions,
} from "@tanstack/react-virtual";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";
import { PageItem } from "~/features/chat/hooks/useDocList";
import type { TextSnipState } from "../../hooks/useTextSnip";
import { DocViewerItem } from "./DocViewerItem";
import { calculateItemHeight } from "./calculateItemHeight";

const OVERSCAN = 1;
const PADDING_X = 12;
const PADDING_BOTTOM = 12;
const ANIMATION_DURATION_MS = 800;

function easeInOutQuint(t: number) {
  return t < 0.5 ? 16 * t * t * t * t * t : 1 + 16 * --t * t * t * t * t;
}

export type DocViewerListHandle = {
  scrollToIndex: (index: number) => void;
};

type DocViewerListProps = {
  pages: PageItem[];
  selectedPageId: string | null;
  togglePageSelection: (pageId: string) => void;
  onScroll: (index: number) => void;
  isZoomActive?: boolean;
  onSelectedPageOffscreen?: () => void;
  organizationId?: string;
  projectId?: string;
  snipState?: TextSnipState;
  onSnipMouseDown?: (
    e: React.MouseEvent,
    containerRect: DOMRect,
    imgRect: DOMRect
  ) => void;
  onSnipMouseMove?: (e: React.MouseEvent, containerRect: DOMRect) => void;
  onSnipMouseUp?: () => void;
};

const DocViewerListComponent = forwardRef<
  DocViewerListHandle,
  DocViewerListProps
>(
  (
    {
      pages,
      selectedPageId,
      togglePageSelection,
      onScroll,
      isZoomActive = false,
      onSelectedPageOffscreen,
      organizationId,
      projectId,
      snipState,
      onSnipMouseDown,
      onSnipMouseMove,
      onSnipMouseUp,
    },
    ref
  ) => {
    const parentRef = useRef<HTMLDivElement>(null);

    const itemWidth = (parentRef.current?.clientWidth ?? 0) - PADDING_X * 2;

    const scrollingRef = useRef<number>();
    const isProgrammaticScrollRef = useRef(true);

    const count = pages.length;

    const itemHeights = useMemo(() => {
      return pages.map((page) =>
        calculateItemHeight(page, itemWidth, PADDING_BOTTOM)
      );
    }, [pages, itemWidth]);

    const estimateSize = useCallback(
      (index: number): number => {
        return itemHeights[index] ?? 0;
      },
      [itemHeights]
    );

    const scrollToFn: VirtualizerOptions<any, any>["scrollToFn"] = useCallback(
      (offset, canSmooth, instance) => {
        const start = parentRef.current?.scrollTop || 0;
        isProgrammaticScrollRef.current = true;

        if (!canSmooth) {
          return elementScroll(offset, canSmooth, instance);
        }

        const startTime = (scrollingRef.current = Date.now());

        const run = () => {
          if (scrollingRef.current !== startTime) return;
          const now = Date.now();
          const elapsed = now - startTime;
          const duration = 800;
          const progress = easeInOutQuint(Math.min(elapsed / duration, 1));
          const interpolated = start + (offset - start) * progress;

          if (elapsed < duration) {
            elementScroll(interpolated, canSmooth, instance);
            requestAnimationFrame(run);
          } else {
            elementScroll(interpolated, canSmooth, instance);
            setTimeout(() => {
              isProgrammaticScrollRef.current = false;
            }, 300);
          }
        };

        requestAnimationFrame(run);
      },
      []
    );

    const rowVirtualizer = useVirtualizer({
      count,
      getScrollElement: () => parentRef.current,
      estimateSize,
      overscan: OVERSCAN,
      scrollToFn,
      getItemKey: (index) => pages[index].pageId,
    });

    useImperativeHandle(
      ref,
      () => ({
        scrollToIndex: (index: number) => {
          rowVirtualizer.scrollToIndex(index, { align: "start" });
        },
      }),
      [rowVirtualizer]
    );

    const virtualItems = rowVirtualizer.getVirtualItems();

    const currentIndex = useMemo(() => {
      const containerHeight =
        rowVirtualizer.options.getScrollElement()?.clientHeight ?? 0;
      const pageChangeLineHeight =
        (rowVirtualizer.scrollOffset ?? 0) + containerHeight * 0.15;
      const topVisibleItem = virtualItems.find(
        (item) =>
          item.start <= pageChangeLineHeight && pageChangeLineHeight < item.end
      );
      return topVisibleItem?.index ?? 0;
    }, [virtualItems, rowVirtualizer]);

    useEffect(() => {
      // Trigger onScroll only during actual user scrolling, not during programmatic scrolls
      // or passive re-renders/measurements that can change currentIndex.
      if (!isProgrammaticScrollRef.current && rowVirtualizer.isScrolling) {
        onScroll(currentIndex);
      }
    }, [currentIndex, onScroll, rowVirtualizer.isScrolling]);

    useEffect(() => {
      rowVirtualizer.measure();
    }, [rowVirtualizer]);

    // Track if selected page is visible and notify when it goes off screen
    const wasSelectedVisibleRef = useRef<boolean>(true);

    const isSelectedPageVisible = useMemo(() => {
      if (!selectedPageId) return false;

      const scrollElement = rowVirtualizer.options.getScrollElement();
      const viewportTop = rowVirtualizer.scrollOffset ?? 0;
      const viewportBottom = viewportTop + (scrollElement?.clientHeight ?? 0);

      // Find the selected page's virtual item
      const selectedIndex = pages.findIndex((p) => p.pageId === selectedPageId);
      if (selectedIndex === -1) return false;

      const selectedItem = virtualItems.find((v) => v.index === selectedIndex);
      if (!selectedItem) return false;

      // Check if the selected item intersects with the viewport
      // (at least partially visible)
      return (
        selectedItem.end > viewportTop && selectedItem.start < viewportBottom
      );
    }, [selectedPageId, pages, virtualItems, rowVirtualizer]);

    useEffect(() => {
      if (!onSelectedPageOffscreen || !selectedPageId) {
        wasSelectedVisibleRef.current = isSelectedPageVisible;
        return;
      }

      const wasVisible = wasSelectedVisibleRef.current;
      // Only trigger when transitioning from visible to not visible
      if (wasVisible && !isSelectedPageVisible) {
        onSelectedPageOffscreen();
      }

      wasSelectedVisibleRef.current = isSelectedPageVisible;
    }, [isSelectedPageVisible, onSelectedPageOffscreen, selectedPageId]);

    return (
      <>
        <div
          ref={parentRef}
          className="w-full h-full pt-[60px] overflow-y-auto"
        >
          {parentRef.current && (
            <div
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: "100%",
                position: "relative",
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const page = pages[virtualRow.index];
                const isSelected = selectedPageId === page.pageId;
                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                      padding: `0 ${PADDING_X}px ${PADDING_BOTTOM}px`,
                      transition: `transform ${ANIMATION_DURATION_MS}ms ease, height ${ANIMATION_DURATION_MS}ms ease`,
                      willChange: "transform, height",
                    }}
                  >
                    <DocViewerItem
                      page={page}
                      isSelected={isSelected}
                      isCurrent={currentIndex === virtualRow.index}
                      togglePageSelection={togglePageSelection}
                      canLoad={!rowVirtualizer.isScrolling}
                      isZoomActive={isZoomActive && isSelected}
                      organizationId={organizationId}
                      projectId={projectId}
                      snipState={isSelected ? snipState : undefined}
                      onSnipMouseDown={onSnipMouseDown}
                      onSnipMouseMove={onSnipMouseMove}
                      onSnipMouseUp={onSnipMouseUp}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>
    );
  }
);

export const DocViewerList = memo(DocViewerListComponent);
